import React, { useEffect, useState } from "react";
import { SectionLabel, Reveal } from "@/lib/uiHelpers";
import { BUSINESS } from "@/constants/landingCopy";
import { Phone } from "lucide-react";

// Module-level stable references (avoids missing-dependency warnings).
const CALENDAR_SRC = BUSINESS.calendarSrc;
const CALENDAR_SCRIPT = BUSINESS.calendarScript;

function useGhlCalendar() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!document.querySelector(`script[src="${CALENDAR_SCRIPT}"]`)) {
      const s = document.createElement("script");
      s.src = CALENDAR_SCRIPT;
      s.type = "text/javascript";
      s.async = true;
      document.body.appendChild(s);
    }
    // Safety: reveal iframe even if onLoad doesn't fire.
    const t = setTimeout(() => setLoaded(true), 2500);
    return () => clearTimeout(t);
  }, []);

  return { loaded, markLoaded: () => setLoaded(true) };
}

function CalendarFrame({ loaded, onLoad }) {
  return (
    <div
      className="relative rounded-2xl overflow-hidden bg-white"
      style={{ border: "1px solid #ece7dc", boxShadow: "0 20px 50px -28px rgba(26,43,74,0.25)" }}
    >
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "#fff" }}>
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 rounded-full animate-spin" style={{ border: "3px solid #F4F0E8", borderTopColor: "#F5A623" }} />
            <span className="font-body text-sm" style={{ color: "#7F8C8D" }}>Loading available times&hellip;</span>
          </div>
        </div>
      )}
      <iframe
        src={CALENDAR_SRC}
        title="Book your free Maui StarPower discovery call"
        id="msgsndr-calendar"
        scrolling="no"
        onLoad={onLoad}
        style={{ width: "100%", border: "none", overflow: "hidden", minHeight: "720px", display: "block" }}
      />
    </div>
  );
}

function FallbackPhone() {
  return (
    <div
      className="rounded-xl px-5 py-4 flex flex-col sm:flex-row items-center justify-center gap-2 text-center"
      style={{ background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.3)" }}
    >
      <span className="font-body font-semibold" style={{ color: "#E74C3C" }}>
        <span aria-hidden="true">&#128308;</span> Don&apos;t see a time that works? Call or text us directly:
      </span>
      <a
        href={BUSINESS.phoneHref}
        data-testid="fallback-phone"
        className="font-head font-extrabold inline-flex items-center gap-1.5"
        style={{ color: "#1A2B4A" }}
      >
        <Phone size={16} /> {BUSINESS.phone}
      </a>
      <span className="font-body text-sm" style={{ color: "#7F8C8D" }}>&mdash; we&apos;ll get you scheduled same-day.</span>
    </div>
  );
}

export default function BookingCalendar() {
  const { loaded, markLoaded } = useGhlCalendar();

  return (
    <section id="booking" className="section-pad" style={{ backgroundColor: "#FAFAF8" }}>
      <div className="container-max">
        <Reveal className="text-center max-w-2xl mx-auto">
          <SectionLabel>Next Step</SectionLabel>
          <h2 className="font-head font-extrabold text-3xl md:text-5xl" style={{ color: "#1A2B4A" }}>
            Select Your Discovery Call Time
          </h2>
          <p className="font-body mt-4 text-base md:text-lg" style={{ color: "#7F8C8D" }}>
            This is a 10-minute call &mdash; no sales pressure, no commitment. We&apos;ll pull up
            your home&apos;s data and show you exactly what the program looks like for your specific
            property.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <CalendarFrame loaded={loaded} onLoad={markLoaded} />
        </Reveal>

        <Reveal delay={0.15} className="mt-6">
          <FallbackPhone />
        </Reveal>

        <p className="font-body text-center text-sm mt-4 max-w-2xl mx-auto" style={{ color: "#7F8C8D" }}>
          Financing options and final terms are confirmed during your discovery call. Estimates
          shown are illustrative &mdash; your offer may vary.
        </p>
      </div>
    </section>
  );
}
